import {
    Component,
    CUSTOM_ELEMENTS_SCHEMA,
    DestroyRef,
    inject,
    OnInit,
    signal,
    ChangeDetectionStrategy
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ShButtonComponent, ShuiModalRef } from '@gbr-monorepo/gbr-shui';
import { TranslateModule } from '@ngx-translate/core';
import { InputText } from '@shui/angular';
import '@shui/core/sh-datepicker';
import '@shui/core/sh-input-number';
import { map } from 'rxjs/operators';
import { parseDateValue } from '../../../../shared/utils/parse-date-value';
@Component({
    selector: 'app-auto-select-applications-dialog',
    imports: [TranslateModule, ReactiveFormsModule, ShButtonComponent, InputText],
    templateUrl: './auto-select-applications-dialog.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class AutoSelectApplicationsDialogComponent implements OnInit {
    private readonly modalRef = inject<ShuiModalRef<{ applicationCutOffDate: Date; percentage: number }>>(ShuiModalRef);
    private readonly destroyRef = inject(DestroyRef);
    protected readonly autoselectFormGroup = new FormGroup({
        applicationCutOffDate: new FormControl<string | null>(null, [Validators.required]),
        percentage: new FormControl<number | null>(null, [Validators.required, Validators.min(1), Validators.max(100)])
    });
    protected readonly applicationCutOffDateErrorSignal = signal<string>('');
    protected readonly percentageErrorSignal = signal<string>('');

    public onClose(result?: string): void {
        if (result === 'confirm') {
            this.validate('applicationCutOffDate');
            this.validate('percentage');

            if (this.canConfirm()) {
                const { applicationCutOffDate, percentage } = this.autoselectFormGroup.value;
                const parsedDate = parseDateValue(applicationCutOffDate);

                if (!parsedDate) {
                    return;
                }

                this.modalRef.close({
                    applicationCutOffDate: parsedDate,
                    percentage: percentage as number
                });
                return;
            }
        }

        this.modalRef.close();
    }

    ngOnInit(): void {
        this.initializeErrorMessageListeners();
    }

    protected canConfirm(): boolean {
        const { applicationCutOffDate, percentage } = this.autoselectFormGroup.value;
        const parsed = this.parsePercentage(percentage);
        return parseDateValue(applicationCutOffDate) !== null && parsed !== null && parsed >= 1 && parsed <= 100;
    }

    private validate(control: 'applicationCutOffDate' | 'percentage'): void {
        if (control === 'applicationCutOffDate') {
            this.applicationCutOffDateErrorSignal.set(
                this.autoselectFormGroup.get('applicationCutOffDate')?.value ? '' : 'required'
            );
        }

        if (control === 'percentage') {
            const percentageRaw = this.autoselectFormGroup.get('percentage')?.value;
            const parsed = this.parsePercentage(percentageRaw);

            if (parsed === null) {
                this.percentageErrorSignal.set('required');
                return;
            }

            if (parsed < 1 || parsed > 100) {
                this.percentageErrorSignal.set('seasons.season-overview.dialogs.errors.percentage-out-of-range');
                return;
            }

            this.percentageErrorSignal.set('');
        }
    }

    private parsePercentage(value: unknown): number | null {
        if (value === undefined || value === null || value === '') return null;
        if (typeof value === 'number') return Number.isNaN(value) ? null : value;
        const n = Number(String(value));
        return Number.isNaN(n) ? null : n;
    }

    private initializeErrorMessageListeners(): void {
        this.autoselectFormGroup
            .get('applicationCutOffDate')
            ?.valueChanges.pipe(
                map(() => this.validate('applicationCutOffDate')),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe();

        this.autoselectFormGroup
            .get('percentage')
            ?.valueChanges.pipe(
                map(() => this.validate('percentage')),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe();
    }

    protected onPercentageChanged(event: Event): void {
        const customEvent = event as CustomEvent<string | number | undefined>;
        const target = event.target as (HTMLElement & { value?: string | number }) | null;
        const value = customEvent.detail ?? target?.value ?? target?.getAttribute?.('value');
        const parsed = this.parsePercentage(value);
        const control = this.autoselectFormGroup.get('percentage');

        if (control?.value === parsed) {
            return;
        }

        control?.setValue(parsed, { emitEvent: false });
        this.validate('percentage');
    }

    protected onDateChanged(event: Event): void {
        const ce = event as CustomEvent<unknown>;
        const target = event.target as (HTMLElement & { standardValue?: unknown[]; value?: string }) | null;
        let value: string | string[] | null | undefined = undefined;

        if (ce && (ce as CustomEvent<Record<string, unknown>>).detail !== undefined) {
            value = (ce as CustomEvent<unknown>).detail as unknown as string | string[] | null | undefined;
        }

        if ((value === undefined || value === null) && target) {
            value = (target as HTMLElement & { value?: string }).value ?? target.getAttribute?.('value') ?? undefined;
            if (!value && Array.isArray(target.standardValue) && target.standardValue.length > 0) {
                const first = target.standardValue[0];
                if (typeof first === 'string') {
                    value = first;
                } else if (first && typeof (first as Date).toISOString === 'function') {
                    value = (first as Date).toISOString().split('T')[0];
                }
            }
        }

        if (Array.isArray(value)) {
            value = value[0] ?? null;
        }

        const normalizedValue = value ?? null;
        const control = this.autoselectFormGroup.get('applicationCutOffDate');

        if (control?.value === normalizedValue) {
            return;
        }

        control?.setValue(normalizedValue, { emitEvent: false });
        this.validate('applicationCutOffDate');
    }
}
